import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ThemeState {
  darkMode: boolean;
  toggleDarkMode: () => void;
  setDarkMode: (isDark: boolean) => void;
  initializeTheme: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      darkMode: false,

      toggleDarkMode: () => {
        const { darkMode } = get();
        const newDarkMode = !darkMode;
        set({ darkMode: newDarkMode });
        applyTheme(newDarkMode);
      },

      setDarkMode: (isDark: boolean) => {
        set({ darkMode: isDark });
        applyTheme(isDark);
      },

      initializeTheme: () => {
        const { darkMode } = get();
        
        // If no stored preference, use system preference
        if (typeof window !== 'undefined') {
          const stored = localStorage.getItem('theme-storage');
          let initialDarkMode = darkMode;
          
          if (!stored) {
            initialDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
            set({ darkMode: initialDarkMode });
          }
          
          applyTheme(initialDarkMode);
          
          // Listen for system theme changes
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          const handleChange = (e: MediaQueryListEvent) => {
            const currentStored = localStorage.getItem('theme-storage');
            if (!currentStored) {
              set({ darkMode: e.matches });
              applyTheme(e.matches);
            }
          };

          mediaQuery.addEventListener('change', handleChange);
          
          // Return cleanup function
          return () => mediaQuery.removeEventListener('change', handleChange);
        }
      },
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({
        darkMode: state.darkMode,
      }),
    }
  )
);

// Helper function to apply theme to DOM
const applyTheme = (isDark: boolean) => {
  if (typeof window !== 'undefined') {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
};
